import { registeredAssetVenueById } from '../utils/assetThumbnailStore';
import {
  favoriteAssetSnapshotIsComplete,
  prepareFavoriteAssetSnapshot,
  type FavoriteAssetEntry,
} from './favoriteAssetsStorage';
import type { FavouriteWireRow } from '../api/favouritesApi';
import { getAssetById } from '../api/getAssetById';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { assetRecordMutationId, assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import { normalizeAssetLookupKey } from '../utils/bookingAssetThumbnailCache';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function lookupKeys(id: string): string[] {
  const trimmed = id.trim();
  if (!trimmed) {
    return [];
  }
  return [trimmed.toLowerCase(), normalizeAssetLookupKey(trimmed)];
}

function coerceAssetId(raw: unknown): string | null {
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/** Merge multiple local favourite buckets without duplicate asset ids. */
export function combineLocalFavoriteCaches(
  ...sources: readonly (readonly FavoriteAssetEntry[])[]
): FavoriteAssetEntry[] {
  const seen = new Set<string>();
  const out: FavoriteAssetEntry[] = [];
  for (const source of sources) {
    for (const entry of source) {
      const id = entry.id.trim();
      if (!id) {
        continue;
      }
      const key = normalizeAssetLookupKey(id);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(entry);
    }
  }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

export function favoriteEntryById(
  entries: readonly FavoriteAssetEntry[],
  assetId: string,
): FavoriteAssetEntry | null {
  const queryKeys = new Set(lookupKeys(assetId));
  for (const entry of entries) {
    for (const candidate of favoriteEntryIdCandidates(entry)) {
      if (lookupKeys(candidate).some((key) => queryKeys.has(key))) {
        return entry;
      }
    }
  }
  return null;
}

/** Prefer the entry whose asset snapshot has the most display metadata. */
export function preferRicherFavoriteEntry(
  preferred: FavoriteAssetEntry,
  alternate: FavoriteAssetEntry,
): FavoriteAssetEntry {
  const preferredComplete = favoriteAssetSnapshotIsComplete(preferred.asset);
  const alternateComplete = favoriteAssetSnapshotIsComplete(alternate.asset);
  if (preferredComplete && !alternateComplete) {
    return preferred;
  }
  if (alternateComplete && !preferredComplete) {
    return alternate;
  }
  const mergedAsset = prepareFavoriteAssetSnapshot({
    ...alternate.asset,
    ...preferred.asset,
  });
  const resolvedId =
    assetRecordMutationId(mergedAsset) ??
    assetRecordMutationId(preferred.asset) ??
    assetRecordMutationId(alternate.asset) ??
    (preferred.id.trim() || alternate.id.trim());
  return {
    id: resolvedId,
    asset: mergedAsset,
    savedAt: Math.max(preferred.savedAt, alternate.savedAt),
  };
}

function stubAssetForId(assetId: string): Record<string, unknown> {
  return prepareFavoriteAssetSnapshot({
    asset_id: assetId,
    id: assetId,
  });
}

function favoriteEntryIdCandidates(entry: FavoriteAssetEntry): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (raw: unknown) => {
    const id = coerceAssetId(raw);
    if (!id) {
      return;
    }
    const key = normalizeAssetLookupKey(id);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(id);
  };

  add(entry.id);
  const asset = coalesceAssetListRecord(entry.asset);
  add(assetRecordMutationId(asset));
  add(assetRecordPrimaryId(asset));
  for (const key of ['asset_id', 'assetId', 'AssetId', 'id', 'Id', 'UUID', 'uuid'] as const) {
    add(asset[key]);
  }
  return out;
}

async function loadAssetSnapshot(assetId: string): Promise<Record<string, unknown>> {
  const cachedVenue = registeredAssetVenueById(assetId);
  if (cachedVenue && favoriteAssetSnapshotIsComplete(cachedVenue)) {
    return prepareFavoriteAssetSnapshot(cachedVenue);
  }
  try {
    const loaded = await getAssetById(assetId);
    if (isRecord(loaded)) {
      const prepared = prepareFavoriteAssetSnapshot(coalesceAssetListRecord(loaded));
      if (favoriteAssetSnapshotIsComplete(prepared)) {
        return prepared;
      }
      if (cachedVenue) {
        return prepareFavoriteAssetSnapshot({
          ...cachedVenue,
          ...prepared,
        });
      }
      return prepared;
    }
  } catch {
    /* keep stub / cache */
  }
  if (cachedVenue) {
    return prepareFavoriteAssetSnapshot(cachedVenue);
  }
  return stubAssetForId(assetId);
}

/** Fetch a full asset row for a favourite entry, trying every known id alias. */
export async function hydrateFavoriteEntryAsset(
  entry: FavoriteAssetEntry,
): Promise<FavoriteAssetEntry> {
  if (favoriteAssetSnapshotIsComplete(entry.asset)) {
    const asset = prepareFavoriteAssetSnapshot(entry.asset);
    return {
      id: assetRecordMutationId(asset) ?? entry.id.trim(),
      asset,
      savedAt: entry.savedAt,
    };
  }

  const candidates = favoriteEntryIdCandidates(entry);
  let bestAsset: Record<string, unknown> | null = null;
  let bestId = entry.id.trim();

  for (const candidate of candidates) {
    const loaded = await loadAssetSnapshot(candidate);
    const resolvedId = assetRecordMutationId(loaded) ?? candidate;
    if (favoriteAssetSnapshotIsComplete(loaded)) {
      return {
        id: resolvedId,
        asset: loaded,
        savedAt: entry.savedAt,
      };
    }
    if (!bestAsset) {
      bestAsset = loaded;
      bestId = resolvedId;
    }
  }

  if (bestAsset && favoriteAssetSnapshotIsComplete(bestAsset)) {
    return {
      id: bestId,
      asset: bestAsset,
      savedAt: entry.savedAt,
    };
  }

  if (favoriteAssetSnapshotIsComplete(entry.asset)) {
    return entry;
  }

  return {
    id: bestId,
    asset: stubAssetForId(bestId || entry.id),
    savedAt: entry.savedAt,
  };
}

function snapshotFromLocalSources(
  assetId: string,
  localCache: readonly FavoriteAssetEntry[],
): Record<string, unknown> | null {
  const fromLocal = favoriteEntryById(localCache, assetId)?.asset ?? null;
  if (fromLocal && favoriteAssetSnapshotIsComplete(fromLocal)) {
    return prepareFavoriteAssetSnapshot(fromLocal);
  }
  const fromVenue = registeredAssetVenueById(assetId);
  if (fromVenue && favoriteAssetSnapshotIsComplete(fromVenue)) {
    return prepareFavoriteAssetSnapshot(fromVenue);
  }
  return fromLocal ? prepareFavoriteAssetSnapshot(fromLocal) : null;
}

/**
 * Build local favourite entries from server rows, reusing local/cache snapshots when possible.
 * Fetches GET /assets/:id for IDs that have no usable snapshot yet.
 */
export async function hydrateFavouriteEntriesFromServer(
  serverRows: FavouriteWireRow[],
  localCache: readonly FavoriteAssetEntry[],
): Promise<FavoriteAssetEntry[]> {
  const out: FavoriteAssetEntry[] = [];
  const pending: Array<{ index: number; entry: FavoriteAssetEntry }> = [];

  serverRows.forEach((row, index) => {
    const id = row.assetId.trim();
    if (!id) {
      return;
    }
    const savedAt = row.savedAt ?? favoriteEntryById(localCache, id)?.savedAt ?? Date.now() - index;
    const fromWire = row.asset ? prepareFavoriteAssetSnapshot(row.asset) : null;
    const fromLocal = snapshotFromLocalSources(id, localCache);
    const asset =
      fromWire && favoriteAssetSnapshotIsComplete(fromWire)
        ? fromWire
        : fromLocal && favoriteAssetSnapshotIsComplete(fromLocal)
          ? fromLocal
          : fromWire ?? fromLocal;

    const seedEntry: FavoriteAssetEntry = {
      id,
      asset: asset ?? stubAssetForId(id),
      savedAt,
    };

    if (asset && favoriteAssetSnapshotIsComplete(asset)) {
      const resolvedId = assetRecordMutationId(asset) ?? id;
      out[index] = {
        id: resolvedId,
        asset,
        savedAt,
      };
      return;
    }

    pending.push({ index, entry: seedEntry });
    out[index] = seedEntry;
  });

  const CONCURRENCY = 4;
  for (let i = 0; i < pending.length; i += CONCURRENCY) {
    const chunk = pending.slice(i, i + CONCURRENCY);
    const loaded = await Promise.all(chunk.map((item) => hydrateFavoriteEntryAsset(item.entry)));
    chunk.forEach((item, j) => {
      out[item.index] = loaded[j] ?? out[item.index];
    });
  }

  return out
    .filter((entry): entry is FavoriteAssetEntry => Boolean(entry?.id && entry.asset))
    .sort((a, b) => b.savedAt - a.savedAt);
}

/** Local favourite rows that are not yet on the server (keep until POST succeeds). */
export function localFavouriteEntriesMissingOnServer(
  localCache: readonly FavoriteAssetEntry[],
  serverRows: readonly FavouriteWireRow[],
): FavoriteAssetEntry[] {
  const serverKeys = new Set<string>();
  for (const row of serverRows) {
    for (const key of lookupKeys(row.assetId)) {
      serverKeys.add(key);
    }
  }
  const missing: FavoriteAssetEntry[] = [];
  const seen = new Set<string>();
  for (const entry of localCache) {
    const keys = lookupKeys(entry.id);
    if (keys.some((key) => serverKeys.has(key))) {
      continue;
    }
    const id = entry.id.trim();
    const dedupeKey = normalizeAssetLookupKey(id);
    if (!id || seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    missing.push(entry);
  }
  return missing;
}

/**
 * Server list plus any local-only favourites (pending POST / not yet on server).
 * Local cache wins for removals until DELETE succeeds (pendingRemovalIds).
 */
export async function mergeFavoritesLocalFirst(
  localCache: readonly FavoriteAssetEntry[],
  serverRows: readonly FavouriteWireRow[],
  pendingRemovalIds: readonly string[],
): Promise<FavoriteAssetEntry[]> {
  const removalKeys = new Set<string>();
  for (const id of pendingRemovalIds) {
    for (const key of lookupKeys(id)) {
      removalKeys.add(key);
    }
  }
  const isRemovedLocally = (assetId: string): boolean =>
    lookupKeys(assetId).some((key) => removalKeys.has(key));

  const filteredServer = serverRows.filter((row) => !isRemovedLocally(row.assetId));
  const activeLocal = localCache.filter((entry) => !isRemovedLocally(entry.id));

  const localOnly = localFavouriteEntriesMissingOnServer(activeLocal, filteredServer);

  const CONCURRENCY = 4;
  const hydratedLocalOnly: FavoriteAssetEntry[] = [];
  for (let i = 0; i < localOnly.length; i += CONCURRENCY) {
    const chunk = localOnly.slice(i, i + CONCURRENCY);
    const loaded = await Promise.all(chunk.map((entry) => hydrateFavoriteEntryAsset(entry)));
    hydratedLocalOnly.push(...loaded);
  }

  const hydratedServer = await hydrateFavouriteEntriesFromServer(filteredServer, [
    ...activeLocal,
    ...hydratedLocalOnly,
  ]);

  const out: FavoriteAssetEntry[] = [];
  const seen = new Set<string>();
  const addEntry = (entry: FavoriteAssetEntry) => {
    const key = normalizeAssetLookupKey(entry.id);
    if (!entry.id.trim() || seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(entry);
  };

  for (const localEntry of activeLocal) {
    const fromServer = favoriteEntryById(hydratedServer, localEntry.id);
    const fromLocalOnly = favoriteEntryById(hydratedLocalOnly, localEntry.id);
    let enriched = localEntry;
    if (fromServer) {
      enriched = preferRicherFavoriteEntry(localEntry, fromServer);
    } else if (fromLocalOnly) {
      enriched = preferRicherFavoriteEntry(localEntry, fromLocalOnly);
    }
    addEntry(enriched);
  }

  for (const entry of hydratedLocalOnly) {
    if (!favoriteEntryById(activeLocal, entry.id)) {
      addEntry(entry);
    }
  }

  for (const entry of hydratedServer) {
    if (favoriteEntryById(activeLocal, entry.id)) {
      continue;
    }
    if (favoriteEntryById(hydratedLocalOnly, entry.id)) {
      continue;
    }
    addEntry(entry);
  }

  return out.sort((a, b) => b.savedAt - a.savedAt);
}

/** @deprecated Use {@link mergeFavoritesLocalFirst} — kept for tests. */
export async function mergeHydratedFavouritesWithLocal(
  serverRows: FavouriteWireRow[],
  localCache: readonly FavoriteAssetEntry[],
): Promise<FavoriteAssetEntry[]> {
  return mergeFavoritesLocalFirst(localCache, serverRows, []);
}

/** Local IDs that are not yet on the server (to POST during migration). */
export function localFavouriteIdsMissingOnServer(
  localCache: readonly FavoriteAssetEntry[],
  serverRows: readonly FavouriteWireRow[],
): string[] {
  return localFavouriteEntriesMissingOnServer(localCache, serverRows).map((entry) => entry.id);
}
